import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Typography,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Card,
    CardMedia,
    CardContent,
    CardActions,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    SelectChangeEvent,
    CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { useAppSelector } from '../hooks/useAppSelector';
import { fetchIdeas, createIdea, updateIdea, deleteIdea } from '../store/slices/ideasSlice';
import { fetchFilters, setAppliedFilters, resetFilters } from '../store/slices/filtersSlice';
import { startLoading, stopLoading } from '../store/slices/loadingSlice';
import { Idea, IdeaFormData } from '../types/idea';
import { FilterOption } from '../types/filters';
import { withNavigation } from '../hoc/withNavigation';
import IdeasFilters from '../components/IdeasFilters';
import { RootState } from '../store';
const IdeasPage: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const ideas = useAppSelector((state: RootState) => state.ideas.items);
    const filters = useAppSelector((state: RootState) => state.filters.items);
    const appliedFilters = useAppSelector((state: RootState) => state.filters.appliedFilters);
    const isLoading = useAppSelector((state: RootState) => state.loading.isLoading);

    const [openDialog, setOpenDialog] = React.useState(false);
    const [currentIdea, setCurrentIdea] = React.useState<Idea | null>(null);
    const [formData, setFormData] = React.useState<IdeaFormData>({
        name: '',
        description: '',
        link: '',
        images: '[]',
        holidays: '[]',
        compilations: '[]',
        genders: '[]',
        price: '0',
    });
    const [imageUrl, setImageUrl] = React.useState('');

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }
        dispatch(fetchIdeas(appliedFilters));
        dispatch(fetchFilters());
    }, [navigate, dispatch, appliedFilters]);

    const handleApplyFilters = (filterParams: any) => {
        dispatch(setAppliedFilters(filterParams));
    };

    const handleOpenDialog = (idea?: Idea) => {
        if (idea) {
            setCurrentIdea(idea);
            setFormData({
                name: idea.name,
                description: idea.description,
                link: idea.link,
                images: idea.images,
                holidays: idea.holidays,
                compilations: idea.compilations,
                genders: idea.genders,
                price: idea.price,
            });
        } else {
            setCurrentIdea(null);
            setFormData({
                name: '',
                description: '',
                link: '',
                images: '[]',
                holidays: '[]',
                compilations: '[]',
                genders: '[]',
                price: '0',
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        console.log('Closing dialog...');
        setOpenDialog(false);
        setCurrentIdea(null);
        console.log('Dialog closed');
    };

    const handleSubmit = async () => {
        try {
            console.log('Starting submit...');
            dispatch(startLoading('Saving idea...'));
            if (currentIdea) {
                console.log('Updating idea:', currentIdea.id);
                const result = await dispatch(updateIdea({ id: currentIdea.id, data: formData })).unwrap();
                console.log('Update result:', result);
                if (result && result.id) {
                    console.log('Update successful, closing dialog...');
                    handleCloseDialog();
                    console.log('Refreshing ideas list...');
                    await dispatch(fetchIdeas(appliedFilters)).unwrap();
                    console.log('Ideas list refreshed');
                } else {
                    console.error('Invalid update result:', result);
                }
            } else {
                console.log('Creating new idea...');
                await dispatch(createIdea(formData)).unwrap();
                await dispatch(fetchIdeas(appliedFilters)).unwrap();
                handleCloseDialog();
            }
        } catch (error) {
            console.error('Error in handleSubmit:', error);
        } finally {
            console.log('Stopping loading...');
            dispatch(stopLoading());
        }
    };


    const handleDelete = async (id: number) => {
        if (window.confirm('Вы уверены, что хотите удалить эту идею?')) {
            try {
                dispatch(startLoading('Deleting idea...'));
                await dispatch(deleteIdea(id)).unwrap();
            } catch (error) {
                console.error('Error deleting idea:', error);
            } finally {
                dispatch(stopLoading());
            }
        }
    };

    const getSelectedOptions = (jsonString: string): number[] => {
        try {
            return JSON.parse(jsonString);
        } catch {
            return [];
        }
    };

    const handleOptionChange = (field: keyof IdeaFormData, value: number[]) => {
        setFormData({
            ...formData,
            [field]: JSON.stringify(value),
        });
    };

    const handleAddImage = () => {
        if (imageUrl.trim()) {
            const currentImages = JSON.parse(formData.images);
            const newImages = [...currentImages, imageUrl];
            setFormData({
                ...formData,
                images: JSON.stringify(newImages),
            });
            setImageUrl('');
        }
    };

    const handleRemoveImage = (index: number) => {
        const currentImages = JSON.parse(formData.images);
        const newImages = currentImages.filter((_: string, i: number) => i !== index);
        setFormData({
            ...formData,
            images: JSON.stringify(newImages),
        });
    };

    const renderMultiSelect = (field: keyof IdeaFormData, options: FilterOption[], label: string) => {
        const selectedValues = getSelectedOptions(formData[field]);

        return (
            <FormControl fullWidth>
                <InputLabel>{label}</InputLabel>
                <Select
                    multiple
                    value={selectedValues}
                    onChange={(e: SelectChangeEvent<number[]>) => handleOptionChange(field, e.target.value as number[])}
                    renderValue={(selected: number[]) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value: number) => {
                                const option = options.find(opt => opt.id === value);
                                return (
                                    <Chip
                                        key={value}
                                        label={`${option?.icon || ''} ${option?.title || value}`}
                                        size="small"
                                    />
                                );
                            })}
                        </Box>
                    )}
                >
                    {options.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span>{option.icon}</span>
                                <span>{option.title}</span>
                            </Box>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    };

    return (
        <Box sx={{ width: '100%' }}>
            {isLoading && (
                <Box sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 9999
                }}>
                    <CircularProgress />
                </Box>
            )}

            <Box sx={{ mb: 4 }}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 2
                }}>
                    <Box sx={{ flex: 1 }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2
                        }}>
                            <Typography variant="h4">Идеи</Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenDialog()}
                            >
                                Добавить идею
                            </Button>
                        </Box>
                        <IdeasFilters
                            filters={filters}
                            onApplyFilters={handleApplyFilters}
                        />
                    </Box>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {ideas?.filter(Boolean).map((idea: Idea) => {
                    const images = idea.images ? JSON.parse(idea.images) : [];
                    const hasImage = images.length > 0;

                    return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={idea.id}>
                            <Card
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    position: 'relative',
                                    '&:hover': { boxShadow: 6 },
                                }}
                            >
                                {hasImage ? (
                                    <CardMedia
                                        component="img"
                                        height="200"
                                        image={images[0]}
                                        alt={idea.name}
                                        sx={{ objectFit: 'cover' }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            height: 200,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                        }}
                                    >
                                        <ImageIcon sx={{ fontSize: 80, color: 'rgba(0, 0, 0, 0.2)' }} />
                                    </Box>
                                )}
                                <Tooltip title="Удалить">
                                    <IconButton
                                        onClick={() => handleDelete(idea.id)}
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                            color: 'error.main',
                                            '&:hover': {
                                                backgroundColor: 'error.main',
                                                color: 'white',
                                            },
                                        }}
                                        size="small"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Typography gutterBottom variant="h6" component="div" noWrap>
                                        {idea.name}
                                    </Typography>
                                    <Typography
                                        variant="h5"
                                        color="primary"
                                        sx={{ fontWeight: 'bold', mb: 1 }}
                                    >
                                        {parseFloat(idea.price).toFixed(2)} ₽
                                    </Typography>
                                </CardContent>
                                <CardActions sx={{ justifyContent: 'flex-start', p: 2 }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<EditIcon />}
                                        onClick={() => handleOpenDialog(idea)}
                                        sx={{
                                            borderRadius: '8px',
                                            textTransform: 'none',
                                            fontSize: '0.9rem',
                                        }}
                                    >
                                        Редактировать
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {currentIdea ? 'Редактировать идею' : 'Добавить идею'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        <TextField
                            label="Название"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            fullWidth
                        />
                        <TextField
                            label="Описание"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            fullWidth
                            multiline
                            rows={4}
                            helperText={`${formData.description.length}/150 символов`}
                            error={formData.description.length > 150}
                            inputProps={{
                                maxLength: 150
                            }}
                        />
                        <TextField
                            label="Ссылка"
                            value={formData.link}
                            onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                            fullWidth
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Typography variant="subtitle1">Изображения</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    label="URL изображения"
                                    value={imageUrl}
                                    onChange={(e) => setImageUrl(e.target.value)}
                                    fullWidth
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleAddImage}
                                    disabled={!imageUrl.trim()}
                                >
                                    Добавить
                                </Button>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                {JSON.parse(formData.images).map((url: string, index: number) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            position: 'relative',
                                            width: 150,
                                            height: 150,
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <img
                                            src={url}
                                            alt={`Изображение ${index + 1}`}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                            }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => handleRemoveImage(index)}
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                },
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                        {renderMultiSelect('genders', filters.genders, 'Пол')}
                        {renderMultiSelect('holidays', filters.holidays, 'Праздники')}
                        {renderMultiSelect('compilations', filters.compilation, 'Подборки')}
                        <TextField
                            label="Цена"
                            type="number"
                            value={formData.price}
                            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Отмена</Button>
                    <Button onClick={handleSubmit} variant="contained">
                        Сохранить
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default withNavigation(IdeasPage);
